import type { PipelineTask } from '../types/pipeline';
import type { VirtualUser } from '../types/vuser';
import { getDefaultTrainingDateRange } from './defaultTrainingDateRange';
import {
  FINE_TUNING_MODEL_NAME_MAX_LEN,
  listFineTuningBaseModelGroups,
  preparePipelineFineTuningTaskConfig,
  resolveFineTuningModelNamePrefix,
} from './fineTuningAgentPayload';

export interface TrainingPeriod {
  from: string;
  to: string;
}

export const PIPELINE_PERIOD_DAYS_MIN = 1;
export const PIPELINE_PERIOD_DAYS_MAX = 365;
export const PIPELINE_PERIOD_DAYS_DEFAULT = 30;

export function parsePipelinePeriodDays(value: unknown): number | null {
  if (value === '' || value == null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  if (n < PIPELINE_PERIOD_DAYS_MIN || n > PIPELINE_PERIOD_DAYS_MAX) return null;
  return n;
}

/** Coerce stored draft values to an API-safe integer in [1, 365]. */
export function normalizePipelinePeriodDaysForSave(value: unknown): number {
  return parsePipelinePeriodDays(value) ?? PIPELINE_PERIOD_DAYS_DEFAULT;
}

export function validatePipelineTaskPeriodDays(
  taskType: string,
  config: Record<string, unknown>
): string | null {
  const type = normalizeTaskType(taskType as PipelineTask['type']);
  if (type !== 'load_integration_datasets' && type !== 'select_training_datasets') return null;
  if (hasTrainingPeriodObject(config)) return null;

  const value = config.period_days;
  if (value === '' || value == null) return null;

  if (parsePipelinePeriodDays(value) == null) {
    return `Period days must be a whole number between ${PIPELINE_PERIOD_DAYS_MIN} and ${PIPELINE_PERIOD_DAYS_MAX}.`;
  }
  return null;
}

export function isValidTrainingPeriodIso(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const ms = Date.parse(trimmed);
  return Number.isFinite(ms);
}

export function normalizeTrainingPeriodIso(value: unknown): string | null {
  if (!isValidTrainingPeriodIso(value)) return null;
  return new Date(String(value).trim()).toISOString();
}

export function hasTrainingPeriodObject(config: Record<string, unknown>): boolean {
  return Boolean(
    config.training_period &&
      typeof config.training_period === 'object' &&
      !Array.isArray(config.training_period)
  );
}

export function getTrainingPeriodFromConfig(
  config: Record<string, unknown>
): { from?: string; to?: string } {
  if (!hasTrainingPeriodObject(config)) return {};
  return config.training_period as { from?: string; to?: string };
}

export function isTrainingPeriodComplete(period: { from?: string; to?: string }): boolean {
  return (
    isValidTrainingPeriodIso(period.from) &&
    isValidTrainingPeriodIso(period.to) &&
    Date.parse(String(period.from).trim()) <= Date.parse(String(period.to).trim())
  );
}

/** Default custom-range window (platform data start → end of tomorrow). */
export function defaultTrainingPeriodRange(_days = 30): TrainingPeriod {
  return getDefaultTrainingDateRange();
}

/** Normalize or drop invalid training_period before API save. */
export function sanitizeTrainingPeriodForSave(period: unknown): TrainingPeriod | undefined {
  if (!period || typeof period !== 'object' || Array.isArray(period)) return undefined;
  const raw = period as { from?: unknown; to?: unknown };
  const fromIso = normalizeTrainingPeriodIso(raw.from);
  const toIso = normalizeTrainingPeriodIso(raw.to);
  if (!fromIso || !toIso) return undefined;
  if (new Date(fromIso).getTime() > new Date(toIso).getTime()) return undefined;
  return { from: fromIso, to: toIso };
}

function normalizeTaskType(type: PipelineTask['type']): string {
  return type === 'fine_tune' ? 'fine_tuning' : type;
}

function taskStepLabel(type: string): string {
  if (type === 'load_integration_datasets') return 'Load Integration Datasets';
  if (type === 'generate_vuser_datasets') return 'Generate VUser Datasets';
  if (type === 'select_training_datasets') return 'Select Training Datasets';
  return type.replace(/_/g, ' ');
}

export function virtualUserDisplayName(user: VirtualUser): string {
  return user.name?.trim() || user.description?.trim() || user.id;
}

export function listVirtualUsersMissingPersona(
  virtualUserIds: string[],
  virtualUsers: VirtualUser[]
): VirtualUser[] {
  return virtualUserIds
    .map((id) => virtualUsers.find((u) => u.id === id))
    .filter((u): u is VirtualUser => Boolean(u && u.type === 'user' && !u.persona_id));
}

export function validatePipelineTaskVirtualUsers(
  taskType: string,
  config: Record<string, unknown>,
  virtualUsers: VirtualUser[]
): string | null {
  const type = normalizeTaskType(taskType as PipelineTask['type']);
  if (type !== 'generate_vuser_datasets') return null;

  const virtualUserIds = Array.isArray(config.virtual_user_ids)
    ? (config.virtual_user_ids as string[]).filter(Boolean)
    : [];

  if (virtualUserIds.length === 0) {
    return 'Select at least one virtual user, or disable this step.';
  }

  const missing = listVirtualUsersMissingPersona(virtualUserIds, virtualUsers);
  if (missing.length === 0) return null;

  const names = missing.map(virtualUserDisplayName).join(', ');
  return `Virtual user must have a persona: ${names}. Assign a persona in Virtual User Service.`;
}

export function validatePipelineTaskTrainingPeriod(
  taskType: string,
  config: Record<string, unknown>
): string | null {
  const type = normalizeTaskType(taskType as PipelineTask['type']);
  if (type !== 'load_integration_datasets' && type !== 'select_training_datasets') return null;
  if (!hasTrainingPeriodObject(config)) return null;

  const period = getTrainingPeriodFromConfig(config);
  const fromRaw = period.from?.trim() ?? '';
  const toRaw = period.to?.trim() ?? '';

  if (!fromRaw && !toRaw) {
    return 'Select both From and To dates, or switch to Last N days.';
  }
  if (!isValidTrainingPeriodIso(fromRaw)) {
    return 'From date is missing or invalid.';
  }
  if (!isValidTrainingPeriodIso(toRaw)) {
    return 'To date is missing or invalid.';
  }
  if (Date.parse(fromRaw) > Date.parse(toRaw)) {
    return 'From date must be before To date.';
  }
  return null;
}

export interface ValidatePipelineTasksOptions {
  virtualUsers?: VirtualUser[];
}

export function validatePipelineTaskFineTuning(
  taskType: string,
  config: Record<string, unknown>
): string | null {
  const type = normalizeTaskType(taskType as PipelineTask['type']);
  if (type !== 'fine_tuning') return null;

  const prepared = preparePipelineFineTuningTaskConfig(config);
  const modelName = resolveFineTuningModelNamePrefix(prepared.model_name);
  if (!modelName.trim()) {
    return 'Model name prefix is required.';
  }
  if (modelName.length > FINE_TUNING_MODEL_NAME_MAX_LEN) {
    return `Model name prefix must be at most ${FINE_TUNING_MODEL_NAME_MAX_LEN} characters.`;
  }

  const agents = Array.isArray(prepared.agents) ? prepared.agents : [];
  if (agents.length === 0) {
    return 'No trainable agents in the integration configurator. Mark at least one agent as trainable.';
  }

  if (prepared.group_by_base_model === true) {
    const groups = listFineTuningBaseModelGroups(
      agents as Array<{ agent_name: string; base_model?: string }>,
      modelName
    );
    if (groups.some((group) => !group.model_name.trim())) {
      return 'Each base model group needs a fine-tuned model name. Set a model name prefix.';
    }
  }

  return null;
}

export function validatePipelineTasks(
  tasks: PipelineTask[],
  options?: ValidatePipelineTasksOptions
): string | null {
  const virtualUsers = options?.virtualUsers ?? [];

  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];
    if (!task.enabled) continue;
    const type = normalizeTaskType(task.type);
    const config = task.config as Record<string, unknown>;

    const periodErr =
      validatePipelineTaskTrainingPeriod(type, config) ||
      validatePipelineTaskPeriodDays(type, config);
    if (periodErr) {
      return `Step ${i + 1} (${taskStepLabel(type)}): ${periodErr}`;
    }

    const vuserErr = validatePipelineTaskVirtualUsers(type, config, virtualUsers);
    if (vuserErr) {
      return `Step ${i + 1} (${taskStepLabel(type)}): ${vuserErr}`;
    }

    const fineTuningErr = validatePipelineTaskFineTuning(type, config);
    if (fineTuningErr) {
      return `Step ${i + 1} (${taskStepLabel(type)}): ${fineTuningErr}`;
    }
  }
  return null;
}
